import type { AxiosResponse } from 'axios'
import type { Location, NavigateFunction } from 'react-router-dom'
import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects'

import type { FilterOptions, SyncFiltersPayload } from '@webWorkers'
import { parseFiltersFromUrlWorkerRunner } from '@webWorkers'
import type { CommonAxiosPayload, ExportBackgroundActionRequestPayload } from '@services/types'

import {
  downloadFileWithURL,
  getActiveCompany,
  getActiveCompanyId,
  getBankTransactionParamsFromStore,
  getErrorMessage,
  getFormErrors,
  getPaidThroughParamsFromStore,
  getPaymentTransactionParamsFromStore,
  getUrlFilterOptionsFromStore,
} from '@helpers'

import type { CreatePaymentPayload, EditPaymentPayload } from '@contexts/PaymentProvider/types'

import type { PaidThroughFormValues } from '@components/dialogs/PaidThroughEditorDialog/types'
import type { SyncFiltersConfig } from '@components/filters/types'
import type { InvoicePayment } from '@oldComponents/forms/DetailsForm/elements/InvoicePaymentDetailsTable'

import type { FiltersStateKey } from '@constants'
import { BackgroundProcessActions } from '@constants'

import { callUrl } from '../common/api'
import expenseActions from '../expense/actions'
import filtersActions from '../filters/actions'
import incomeActions from '../income/actions'
import taxActions from '../tax/actions'
import actions from './actions'
import * as api from './api'
import { BankTransactionsExportBackgroundAction, PaymentTransactionsExportBackgroundAction } from './backgroundActions'
import type { BankTransactionsApiResponse, PaidThroughApiResponse, PaymentTransactionsApiResponse } from './types'

function* initPaidThroughListPageLoadSaga({
  payload: { config, location, navigate },
  meta: { resolve, reject },
}: AsyncSagaAction<{
  config: SyncFiltersConfig
  location: Location<unknown>
  navigate: NavigateFunction
}>) {
  try {
    //* call worker
    const { filters, params, validationLevel }: SyncFiltersPayload = yield call(parseFiltersFromUrlWorkerRunner, {
      config,
      filterOptions: {} as FilterOptions,
      location,
    })
    yield all([
      put(filtersActions.initPaidThroughListFiltersFromUrl.request({ filters })),
      put(filtersActions.initPaidThroughListParamsFromUrl.request(params)),
    ])
    yield call(resolve, validationLevel)
    yield put(actions.fetchPaidThroughs.request({ navigate }))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// paid through
function* fetchPaidThroughsSaga({ payload: { navigate } }: SagaAction<{ navigate: NavigateFunction }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: ApiPageParams = yield select(getPaidThroughParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<PaidThroughApiResponse> = yield call(api.fetchPaidThroughs, companyId, params)
    yield put(actions.fetchPaidThroughs.success(response.data))
    //* << sync from store to url
    yield put(
      filtersActions.syncFiltersToUrl.request({
        navigate,
        filters: params,
        skipDateFilters: true,
      })
    )
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaidThroughs.failure(errorMsg))
  }
}

function* fetchPaidThroughsListSimpleSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: ApiPageParams = yield select(getPaidThroughParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<PaidThroughApiResponse> = yield call(api.fetchPaidThroughs, companyId, params)
    yield put(actions.fetchPaidThroughs.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaidThroughs.failure(errorMsg))
  }
}

// cursor pagination (next or previous url)
function* fetchPaidThroughsByPagingSaga({ payload: { url } }: SagaAction<{ url: string }>) {
  try {
    const response: AxiosResponse<PaidThroughApiResponse> = yield call(api.fetchDataByUrl, url)
    yield put(actions.fetchPaidThroughs.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaidThroughs.failure(errorMsg))
  }
}

// options
function* fetchPaidThroughOptionsSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaidThroughOption[]> = yield call(api.fetchPaidThroughOptions, companyId, {
      show_express: true,
    })
    yield put(actions.fetchPaidThroughOptions.success(response.data))
  } catch (_error) {
    yield put(actions.fetchPaidThroughOptions.failure())
  }
}

function* removePaidThroughSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    yield call(api.removePaidThrough, companyId, payload)
    yield put(actions.removePaidThrough.success())
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* createPaidThroughSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<PaidThroughFormValues>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaidThroughDetailData> = yield call(api.createPaidThrough, companyId, payload)
    yield put(actions.createPaidThrough.success())
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

function* createPaidThroughOptionSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<PaidThroughFormValues>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaidThroughDetailData> = yield call(api.createPaidThrough, companyId, payload)
    yield put(actions.createPaidThroughOption.success())
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

function* fetchPaidThroughDetailsSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaidThroughDetailData> = yield call(api.fetchPaidThroughDetails, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

//* v2
function* createPaymentV2Saga({ payload, meta: { resolve, reject } }: AsyncSagaAction<CreatePaymentPayload>) {
  try {
    const { id: companyId } = yield select(getActiveCompany)
    const response: AxiosResponse<BackendPaymentsResult> = yield call(api.createPayment, companyId, payload)
    yield put(actions.createPaymentV2.success(payload)) // pass request payload
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

function* successPaymentV2Saga({ payload: { transactionType } }: SagaAction<BackendPaymentsResult>) {
  if (transactionType === 'expense') {
    yield put(expenseActions.triggerExpenseListUpdate.request())
  } else if (transactionType === 'tax') {
    yield put(taxActions.triggerTaxListUpdate.request())
  } else if (transactionType === 'income') {
    yield put(incomeActions.triggerIncomeListUpdate.request())
  }
}

//* v2
function* removePaymentSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<CommonAxiosPayload>) {
  try {
    yield call(callUrl, payload)
    yield put(actions.removePayment.success())
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* initializePaymentSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<PaymentSetupPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaymentSetupResponseData> = yield call(api.initializePayment, companyId, payload)
    if (response.data.transactions.length) {
      const paidThroughOptionsResponse: AxiosResponse<PaidThroughOption[]> = yield call(
        api.fetchPaidThroughOptions,
        companyId
      )
      yield put(actions.fetchPaidThroughOptions.success(paidThroughOptionsResponse.data))
    }
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* fetchPaymentSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<PaymentDetailsResponseData> = yield call(api.fetchPayment, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* updatePaymentSaga({
  payload: { skipStoreUpdate, ...data },
  meta: { resolve, reject },
}: AsyncSagaAction<EditPaymentPayload>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<BackendPaymentsResult> = yield call(api.updatePayment, companyId, data)
    if (!skipStoreUpdate) {
      // need when payment updated from invoice payment history edit flow in expensedetilsform
      yield put(actions.updatePayment.success(response.data))
    }
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

// EXPORT
function* exportPaymentSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<
  CommonAxiosPayload<{
    transfer_type: string | undefined
  }>
>) {
  try {
    // this is a not delayed bg-job on backend so response containes processed data when returns
    const response: AxiosResponse<{ path: string }> = yield call(callUrl, payload)
    yield call(downloadFileWithURL, response) // download file
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// EXPENSE payments from details
function* loadInvoiceTransactionsSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<ItemIdType>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<InvoicePayment[]> = yield call(api.loadInvoiceTransactions, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

// Payment form BALANCE
function* loadPaidThroughProviderBalanceSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<number>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<CurrentBalance> = yield call(api.loadPaidThroughProviderBalance, companyId, payload)
    yield call(resolve, response.data)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

//* BANK TRANSACTIONS
function* initBankTransactionListPageLoadSaga({
  payload: { config, filtersStateKey, location, navigate },
  meta: { resolve, reject },
}: AsyncSagaAction<{
  config: SyncFiltersConfig
  filtersStateKey: FiltersStateKey
  location: Location<unknown>
  navigate: NavigateFunction
}>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const paidThroughOptionsResponse: AxiosResponse<PaidThroughOption[]> = yield call(
      api.fetchPaidThroughOptions,
      companyId,
      {
        show_express: true,
      }
    )
    yield put(actions.fetchPaidThroughOptions.success(paidThroughOptionsResponse.data))

    const filterOptions: FilterOptions = yield select(getUrlFilterOptionsFromStore, filtersStateKey)
    //* call worker
    const { filters, params, validationLevel } = yield call(parseFiltersFromUrlWorkerRunner, {
      config,
      filterOptions,
      location,
    })
    yield all([
      put(
        filtersActions.initBankTransactionListFiltersFromUrl.request({
          filters,
        })
      ),
      put(filtersActions.initBankTransactionListParamsFromUrl.request(params)),
    ])
    yield call(resolve, validationLevel)
    yield put(actions.fetchBankTransactions.request({ navigate }))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* fetchBankTransactionsSaga({ payload: { navigate } }: SagaAction<{ navigate: NavigateFunction }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: Partial<BankTransactionsStoreFilters> = yield select(getBankTransactionParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<BankTransactionsApiResponse> = yield call(
      api.fetchBankTransactions,
      companyId,
      params
    )
    yield put(actions.fetchBankTransactions.success(response.data))
    //* << sync from store to url
    yield put(filtersActions.syncFiltersToUrl.request({ navigate, filters: params }))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchBankTransactions.failure(errorMsg))
  }
}

function* fetchBankTransactionsListSimpleSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: Partial<BankTransactionsStoreFilters> = yield select(getBankTransactionParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<BankTransactionsApiResponse> = yield call(
      api.fetchBankTransactions,
      companyId,
      params
    )
    yield put(actions.fetchBankTransactions.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchBankTransactions.failure(errorMsg))
  }
}

// cursor pagination (next or previous url)
function* fetchBankTransactionsByPagingSaga({ payload: { url } }: SagaAction<{ url: string }>) {
  try {
    const response: AxiosResponse<BankTransactionsApiResponse> = yield call(api.fetchDataByUrl, url)
    yield put(actions.fetchBankTransactions.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchBankTransactions.failure(errorMsg))
  }
}

function* startBankTransactionsExportSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<Pick<ExportBackgroundActionRequestPayload, 'setInfoAlert'>>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const filters: ApiPageParams = yield select(getBankTransactionParamsFromStore)
    const response: BackgroundActionResponse = yield call(
      BankTransactionsExportBackgroundAction.start,
      companyId,
      {
        action: BackgroundProcessActions.BANK_TRANSACTIONS_EXPORT,
        ...filters,
      },
      payload.setInfoAlert
    )
    yield call(resolve, response)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* stopBankTransactionsExportSaga() {
  yield call(BankTransactionsExportBackgroundAction.stop)
}

//* PAYMENT TRANSACTIONS
function* initPaymentTransactionListPageLoadSaga({
  payload: { config, filtersStateKey, location, navigate },
  meta: { resolve, reject },
}: AsyncSagaAction<{
  config: SyncFiltersConfig
  filtersStateKey: FiltersStateKey
  location: Location<unknown>
  navigate: NavigateFunction
}>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const paidThroughOptionsResponse: AxiosResponse<PaidThroughOption[]> = yield call(
      api.fetchPaidThroughOptions,
      companyId,
      {
        show_express: true,
      }
    )
    yield put(actions.fetchPaidThroughOptions.success(paidThroughOptionsResponse.data))

    const filterOptions: FilterOptions = yield select(getUrlFilterOptionsFromStore, filtersStateKey)
    //* call worker
    const { filters, params, validationLevel } = yield call(parseFiltersFromUrlWorkerRunner, {
      config,
      filterOptions,
      location,
    })
    yield all([
      put(
        filtersActions.initPaymentTransactionListFiltersFromUrl.request({
          filters,
        })
      ),
      put(filtersActions.initPaymentTransactionListParamsFromUrl.request(params)),
    ])
    yield call(resolve, validationLevel)
    yield put(actions.fetchPaymentTransactions.request({ navigate }))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* fetchPaymentTransactionsSaga({ payload: { navigate } }: SagaAction<{ navigate: NavigateFunction }>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: Partial<PaymentTransactionsStoreFilters> = yield select(getPaymentTransactionParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<PaymentTransactionsApiResponse> = yield call(
      api.fetchPaymentTransactions,
      companyId,
      params
    )
    yield put(actions.fetchPaymentTransactions.success(response.data))
    //* << sync from store to url
    yield put(filtersActions.syncFiltersToUrl.request({ navigate, filters: params }))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaymentTransactions.failure(errorMsg))
  }
}

function* fetchPaymentTransactionsListSimpleSaga() {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const params: Partial<PaymentTransactionsStoreFilters> = yield select(getPaymentTransactionParamsFromStore, {
      withCursor: true,
    })
    const response: AxiosResponse<PaymentTransactionsApiResponse> = yield call(
      api.fetchPaymentTransactions,
      companyId,
      params
    )
    yield put(actions.fetchPaymentTransactions.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaymentTransactions.failure(errorMsg))
  }
}

function* fetchPaymentTransactionsByPagingSaga({ payload: { url } }: SagaAction<{ url: string }>) {
  try {
    const response: AxiosResponse<PaymentTransactionsApiResponse> = yield call(api.fetchDataByUrl, url)
    yield put(actions.fetchPaymentTransactions.success(response.data))
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield put(actions.fetchPaymentTransactions.failure(errorMsg))
  }
}

function* startPaymentTransactionsExportSaga({
  payload,
  meta: { resolve, reject },
}: AsyncSagaAction<Pick<ExportBackgroundActionRequestPayload, 'setInfoAlert'>>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const filters: ApiPageParams = yield select(getPaymentTransactionParamsFromStore)
    const response: BackgroundActionResponse = yield call(
      PaymentTransactionsExportBackgroundAction.start,
      companyId,
      {
        action: BackgroundProcessActions.PAYMENT_TRANSACTIONS_EXPORT,
        ...filters,
      },
      payload.setInfoAlert
    )
    yield call(resolve, response)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* stopPaymentTransactionsExportSaga() {
  yield call(PaymentTransactionsExportBackgroundAction.stop)
}

function* removePaymentTransactionSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<CommonAxiosPayload>) {
  try {
    yield call(callUrl, payload)
    yield put(actions.removePaymentTransaction.success())
    yield call(resolve)
  } catch (error) {
    const errorMsg = getErrorMessage(error)
    yield call(reject, errorMsg)
  }
}

function* updatePaidThroughSaga({ payload, meta: { resolve, reject } }: AsyncSagaAction<PaidThroughFormValues>) {
  try {
    const companyId: Company['id'] = yield select(getActiveCompanyId)
    const response: AxiosResponse<unknown> = yield call(api.updatePaidThrough, companyId, payload)
    yield put(actions.updatePaidThrough.success())
    yield call(resolve, response.data)
  } catch (error) {
    const formErrors = getFormErrors(error)
    yield call(reject, formErrors)
  }
}

// watcher Saga
export default function* commonSaga() {
  //* PAID THROUGHS
  yield takeLatest(actions.initPaidThroughListPageLoad.REQUEST, initPaidThroughListPageLoadSaga)
  yield takeLatest(actions.fetchPaidThroughs.REQUEST, fetchPaidThroughsSaga)
  yield takeLatest(
    [
      actions.createPaidThrough.SUCCESS,
      actions.updatePaidThrough.SUCCESS,
      actions.removePaidThrough.SUCCESS,
      actions.updatePaidThroughsRowsPerPage.REQUEST,
      actions.resetPaidThroughsPagination.REQUEST,
      filtersActions.updatePaidThroughFilters.REQUEST,
      filtersActions.resetPaidThroughFilters.REQUEST,
    ],
    fetchPaidThroughsListSimpleSaga
  )
  yield takeLatest(
    [actions.fetchPaidThroughOptions.REQUEST, actions.createPaidThroughOption.SUCCESS],
    fetchPaidThroughOptionsSaga
  )
  yield takeLatest(actions.removePaidThrough.REQUEST, removePaidThroughSaga)
  yield takeLatest(actions.createPaidThrough.REQUEST, createPaidThroughSaga)
  yield takeLatest(actions.createPaidThroughOption.REQUEST, createPaidThroughOptionSaga)
  yield takeLatest(actions.fetchPaidThroughDetails.REQUEST, fetchPaidThroughDetailsSaga)
  // cursor pagination
  yield takeLatest(actions.updatePayment.REQUEST, updatePaymentSaga)
  yield takeLatest(actions.removePayment.REQUEST, removePaymentSaga)
  yield takeLatest(actions.initializePayment.REQUEST, initializePaymentSaga)
  yield takeLatest(actions.fetchPayment.REQUEST, fetchPaymentSaga)
  // EXPORT (allow multiple call in parallel request)
  yield takeEvery(actions.exportPayment.REQUEST, exportPaymentSaga)
  // EXPENSE details
  yield takeLatest(actions.loadInvoiceTransactions.REQUEST, loadInvoiceTransactionsSaga)
  // PAYMENT BALANCE
  yield takeLatest(actions.loadPaidThroughProviderBalance.REQUEST, loadPaidThroughProviderBalanceSaga)
  //* BANK TRANSACTIONS
  yield takeLatest(actions.initBankTransactionListPageLoad.REQUEST, initBankTransactionListPageLoadSaga)
  yield takeLatest(actions.fetchBankTransactions.REQUEST, fetchBankTransactionsSaga)
  yield takeLatest(
    [
      actions.resetBankTransactionsPagination.REQUEST,
      actions.updateBankTransactionsRowsPerPage.REQUEST,
      filtersActions.resetBankTransactionsFilters.REQUEST,
      filtersActions.toggleBankTransactionsDateFilter.REQUEST,
      filtersActions.updateBankTransactionsFilters.REQUEST,
    ],
    fetchBankTransactionsListSimpleSaga
  )
  //* PAYMENT TRANSACTIONS
  yield takeLatest(actions.initPaymentTransactionListPageLoad.REQUEST, initPaymentTransactionListPageLoadSaga)
  yield takeLatest(actions.fetchPaymentTransactions.REQUEST, fetchPaymentTransactionsSaga)
  yield takeLatest(
    [
      actions.removePaymentTransaction.SUCCESS,
      actions.resetPaymentTransactionsPagination.REQUEST,
      actions.updatePaymentTransactionsRowsPerPage.REQUEST,
      filtersActions.resetPaymentTransactionsFilters.REQUEST,
      filtersActions.togglePaymentTransactionsDateFilter.REQUEST,
      filtersActions.updatePaymentTransactionsFilters.REQUEST,
    ],
    fetchPaymentTransactionsListSimpleSaga
  )

  // cursor pagination
  yield takeLatest(actions.fetchPaidThroughsByPaging.REQUEST, fetchPaidThroughsByPagingSaga)
  yield takeLatest(actions.fetchBankTransactionsByPaging.REQUEST, fetchBankTransactionsByPagingSaga)
  yield takeLatest(actions.fetchPaymentTransactionsByPaging.REQUEST, fetchPaymentTransactionsByPagingSaga)
  yield takeLatest(actions.createPaymentV2.REQUEST, createPaymentV2Saga)
  yield takeLatest(actions.createPaymentV2.SUCCESS, successPaymentV2Saga)
  yield takeLatest(actions.startBankTransactionsExport.REQUEST, startBankTransactionsExportSaga)
  yield takeLatest(actions.stopBankTransactionsExport.REQUEST, stopBankTransactionsExportSaga)
  yield takeLatest(actions.startPaymentTransactionsExport.REQUEST, startPaymentTransactionsExportSaga)
  yield takeLatest(actions.stopPaymentTransactionsExport.REQUEST, stopPaymentTransactionsExportSaga)
  yield takeLatest(actions.removePaymentTransaction.REQUEST, removePaymentTransactionSaga)
  yield takeLatest(actions.updatePaidThrough.REQUEST, updatePaidThroughSaga)
}
